import { FC, ReactNode, forwardRef, RefObject } from "react";
import { Link, Button } from "./HyperLink.styled";
import { Text } from "@otrium/atoms";
import { AriaButtonProps, AriaLinkOptions, useLink } from "react-aria";
import { FocusableElement } from "@react-types/shared";
import { IColor } from "@otrium/core";
import classNames from "classnames";

interface Props {
  icon?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  color?: string | IColor;
  hasUnderline?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  asLink?: boolean;
  href?: string;
  variant?: string;
}

const HyperLink: FC<Props & AriaButtonProps & AriaLinkOptions> = forwardRef<
  HTMLAnchorElement & HTMLButtonElement,
  Props & AriaButtonProps & AriaLinkOptions
>(
  (
    {
      icon = null,
      className,
      hasUnderline,
      fullWidth,
      children,
      color,
      asLink,
      variant,
      href,
      target,
      rel,
      ...rest
    },
    ref
  ) => {
    // Split data-* attributes out of rest before handing it to useLink — react-
    // aria's hook is strict about its prop shape, and these attributes need to
    // land on the rendered DOM element so callers can attach tracking markup
    // (e.g. data-cnstrc-search-submit-btn).
    const dataAttrs: Record<string, unknown> = {};
    const ariaRest: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) {
      if (k.startsWith("data-")) dataAttrs[k] = v;
      else ariaRest[k] = v;
    }
    const { linkProps } = useLink(
      { ...ariaRest, isDisabled: rest.disabled },
      ref as RefObject<FocusableElement>
    );
    return (
      <>
        {href ? (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          /* @ts-ignore */
          <Link
            {...linkProps}
            ref={ref}
            href={href}
            target={target}
            className={classNames(className, {
              ["with-icon"]: icon,
              ["with-underline"]: hasUnderline,
              ["full-width"]: fullWidth,
            })}
            color={color}
            data-testid="hyper-link"
            {...dataAttrs}
          >
            {icon}
            <Text
              as="span"
              data-testid="hyper-link-content"
              variant={
                variant !== "inherit"
                  ? variant || "body.source.semibold16"
                  : undefined
              }
            >
              {children}
            </Text>
          </Link>
        ) : (
          <Button
            {...rest}
            ref={ref}
            className={classNames(className, {
              ["with-icon"]: icon,
              ["with-underline"]: hasUnderline,
              ["full-width"]: fullWidth,
            })}
            color={color}
            data-testid="hyper-link-button"
          >
            {icon}
            <Text
              as="span"
              data-testid="hyper-link-button-content"
              variant={variant || "body.source.semibold16"}
            >
              {children}
            </Text>
          </Button>
        )}
      </>
    );
  }
);

HyperLink.displayName = "HyperLink";

export default HyperLink;
